import React, {useCallback, useMemo, useState} from 'react';
import {Box, DialogActions, DialogContent, DialogTitle, Divider} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';
import Form, {MuiRFF, Yup} from '../../../../../../components/form';
import ValidateMessages from '../../../../../../../core/models/static/texts/vallidate-messages';
import useIsMounted from '../../../../../../hooks/use-is-mounted';

const formNames = {
    reason: 'reason',
};

const ResolveQueueItemDialog = ({open, item, action, onClose, onResolved}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.admin.reviewQueue'});
    const isMounted = useIsMounted();
    const [submitting, setSubmitting] = useState(false);

    const schema = useMemo(() => Yup.object().shape({
        [formNames.reason]: Yup.string().trim().nullable().required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.reason]: '',
    }), [item?.id, action]);

    /**
     * Submits approve/reject with mandatory reason (mock API).
     */
    const handleSubmit = useCallback(async (values) => {
        if (!item || !action) {
            return;
        }
        setSubmitting(true);
        // TODO: Replace with real API when endpoint is available
        const response = await Promise.resolve({
            resultFlag: true,
            data: {
                itemId: item.id,
                action,
                reason: values[formNames.reason]?.trim(),
            },
        });
        if (!isMounted()) {
            return;
        }
        setSubmitting(false);
        if (response?.resultFlag) {
            onResolved?.(response.data);
            onClose?.();
        }
    }, [action, isMounted, item, onClose, onResolved]);

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (!nextOpen) {
                    onClose?.();
                }
            }}
            maxWidth="sm"
        >
            <DialogTitle>
                {action === 'approve' ? t('approveTitle') : t('rejectTitle')}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={handleSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'biz-point-resolve-queue-item-form'}}
                >
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
                        <MuiRFF.TextField
                            name={formNames.reason}
                            label={t('reasonRequired')}
                            helperText={t('reasonHelper')}
                            required={required[formNames.reason]}
                            multiline
                            rows={3}
                        />
                    </Box>
                </Form>
            </DialogContent>
            <Divider/>
            <DialogActions sx={{p: 0, m: 0, justifyContent: 'space-between'}}>
                <Button
                    variant="dialog"
                    color="primary"
                    borderRadius="0 8px 0 8px"
                    onClick={onClose}
                    disabled={submitting}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant="dialog"
                    color="primary"
                    borderRadius="8px 0 8px 0"
                    type="submit"
                    form="biz-point-resolve-queue-item-form"
                    disabled={submitting}
                >
                    {t('confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ResolveQueueItemDialog;
